import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { ChallengeTypeApi } from '../../types/api/challenges';

import './BoardList.css';
import type { ChallengeLeaderEntryRedux } from '../../types/models/challenges';

import BoardListRow from './BoardListRow';

interface BoardListProps {
  users: ChallengeLeaderEntryRedux[];
  challengeType: ChallengeTypeApi | string;
  userPlace: number | undefined;
  leaders: ChallengeLeaderEntryRedux[];
}

function BoardList({ users, challengeType, userPlace, leaders }: BoardListProps) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLUListElement>(null);

  const dataToMap = useMemo(() => {
    if (leaders.length > 0 && userPlace > 6) {
      return leaders.filter((x) => !users.some((y) => y.place === x.place));
    }
    return [];
  }, [userPlace, leaders, users]);

  useEffect(() => {
    if (listRef.current) {
      const userElement = listRef.current.querySelector('.boardList-row-user');
      if (userElement) {
        const { offsetTop } = userElement as HTMLElement;
        listRef.current.scrollTo(0, offsetTop - 91);
      }
    }
  }, [userPlace]);

  return (
    <div className="boardList-wrapper">
      <div className="boardList-overlay-top" />
      <div className="boardList-overlay-bottom" />
      <ul className="boardList" ref={listRef} aria-label={t('challenge.leaderboard.list.label')}>
        {userPlace > 6 && dataToMap.length > 0 && (
          <div>
            {dataToMap.map((leader) => (
              <BoardListRow
                key={`leader-${leader.place}`}
                user={leader}
                userPlace={userPlace}
                challengeType={challengeType}
                leaderBoard
              />
            ))}
          </div>
        )}
        {userPlace > 9 && dataToMap.length === 3 && users.length > 0 && users[0].place !== 4 && (
          <div className="boardList-dots" aria-hidden="true">
            ...
          </div>
        )}
        {users.map((user) => (
          <BoardListRow
            key={`user-${user.place}`}
            user={user}
            userPlace={userPlace}
            challengeType={challengeType}
          />
        ))}
        {users.length < 5 &&
          Array.from(Array(5 - users.length).keys()).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="boardList-row" key={`empty-${index}`}>
              <div className="boardList-user boardList-user-empty">
                <div className="boardList-avatar" />
              </div>
            </div>
          ))}
      </ul>
    </div>
  );
}

export default BoardList;
